import React, { Fragment, useEffect } from 'react';
import { MenuItem, Select, TextField, FormControl, InputLabel } from '@mui/material';
import { AlertCircle } from 'react-feather';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import { CalendarMonth } from '@mui/icons-material';
import { Modal, ModalHeader, ModalBody, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';
import DateRange from '../dateRange/secondary';
import dateFormat from '../../utils/dateFormat';

const Manage = ({ location }) => {
  useEffect(() => {
    document.title = 'PFSA | School Calendars | Terms';
  }, []);
  const currentYear = new Date();

  const defaultValues = { start_date: '', end_date: '' }; // start>end

  const defaultFilterOptions = {
    years: [currentYear.getFullYear() + 1, currentYear.getFullYear()],
    schoolCalendars: [],
  };
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  // const [appliedFilters, setAppliedFilters] = React.useState(["school_calendar_id", "year"]);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);

  const [tableData, setTableData] = React.useState({ count: 0, terms: [] });

  const [filterOptions, setFilterOptions] = React.useState(defaultFilterOptions);
  const [dates, setDates] = React.useState(defaultValues);
  const [searchInput, setSearchInput] = React.useState({
    school_calendar_id: '',
    year: '',
  });

  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });

  React.useEffect(() => {
    setDataIsFetching(true);
    bulkAxios(['/school-calendars/names', '/school-calendars/terms/years'])
      .then(async (all) => {
        setFilterOptions(({ years }) => ({
          schoolCalendars: all[0].schoolCalendars,
          years: years.concat(all[1].years.map((item) => item.year)),
        }));
        const q = window.location.search.replace('?', '');
        let defaults = {
          school_calendar_id: all[0].schoolCalendars[0]?.id,
          year: currentYear.getFullYear(),
        };
        if (q) {
          setSearchInput((prev) => ({
            ...prev,
            school_calendar_id: new URLSearchParams(location.search).get('school_calendar_id'),
          }));
          defaults = { ...defaults, ...getQueryParams() };
        }

        setSearchInput(defaults);
        try {
          let { data } = await userRequest().get(
            `/school-calendars/terms?per_page=${perPage}&page=${page}&${buildQuery(null, defaults)}`
          );
          setTableData({
            count: data.total,
            terms: data.terms,
          });
        } catch (error) {
          console.log(error);
        }
        setDataIsFetching(false);
      })
      .catch((error) => {
        console.log(error);
        setDataIsFetching(false);
      });
  }, [perPage, page]);

  function bulkAxios(urls = null) {
    if (!urls) return urls;

    let promises = urls.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
    );

    return Promise.all(promises);
  }

  function getFilters() {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'school_calendar_id') {
            return `School Calendar: ${filterOptions.schoolCalendars.find((x) => x.id == value)?.display_name}`;
          } else if (key === 'year') {
            return `Year: ${value}`;
          }
        }
      })
      .filter((x) => x !== undefined);
  }

  const onChangeDate = (name, value) => {
    setDates((pr) => {
      pr[name] = value === null ? '' : value;
      return { ...pr };
    });
  };

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }

    return true;
  }

  const onSubmit = (e) => {
    e.preventDefault();
    if (Object.values(dates).every((d) => !d)) return null;
    toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'create',
    });
    let api = modalState.update ? `/school-calendars/terms/update/${modalState.update}` : '/school-calendars/terms';
    const { school_calendar_id } = searchInput;
    userRequest()
      .post(api, { ...dates, school_calendar_id })
      .then((res) => {
        toast.dismiss('create');
        toast.success(`Successfully ${modalState.update ? 'updated' : 'created'}`, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
        const term = res.data.term;
        let terms = [];
        if (modalState.update) {
          terms = meetFilterConditions(term)
            ? tableData.terms.map((s) => {
                if (s.id === term.id) return term;
                else return s;
              })
            : tableData.terms.filter((s) => s.id !== term.id);
        } else {
          terms = meetFilterConditions(term, false) ? [...tableData.terms, term] : tableData.terms;
        }
        setTableData({
          terms,
          count: terms.length,
        });
        setModalState({ update: true, open: false });
      })
      .catch(({ response }) => {
        toast.dismiss('create');
        toast.error(response.data.message, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
      });
  };

  function getQueryParams() {
    return window.location.search
      .replace('?', '')
      .split('&')
      .reduce((r, e) => {
        const split = e.split('=');
        return (r[split[0]] = decodeURIComponent(split[1])), r;
      }, {});
  }

  function buildQuery(foundFilter = null, vals) {
    return Object.entries(vals ? vals : searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (foundFilter) foundFilter.push(key);
          return encodeURIComponent(key) + '=' + encodeURIComponent(value);
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');
  }

  const onSearch = (e) => {
    e.preventDefault();

    const queryString = buildQuery();

    userRequest()
      .get(`/school-calendars/terms?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          terms: data.terms,
        });
      })
      .catch(({ response }) => {
        console.log(response);
      });
  };

  const onChangeInput = (e, val, name = null) => {
    if (name) {
      setSearchInput({ ...searchInput, [name]: val });
    } else {
      setSearchInput({ ...searchInput, [e.target.name]: e.target.value });
    }

    // Only trigger the filtering logic when the year is changed
    //if (name === 'year') {
    //  console.log('hhhhhhhhhhhhhhhh');
    //  filterTable();
    //} else if (e.target.name === 'year') {
    //  filterTable();
    //  console.log(buildQuery());
    //}
  };

  useEffect(() => {
    filterTable();
  }, [searchInput.year, searchInput.school_calendar_id]);

  const filterTable = () => {
    const queryString = buildQuery();
    userRequest()
      .get(`/school-calendars/terms?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          terms: data.terms,
        });
      })
      .catch(({ response }) => {
        console.log(response);
      });
  };

  const currentCalendar = filterOptions.schoolCalendars.find(
    (cal) => cal.id === searchInput.school_calendar_id
  )?.display_name;

  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      setDates({ start_date: row.start_date, end_date: row.end_date });
    },
  };

  const columns = [
    {
      field: 'display_name',
      headerName: 'Calendar',
      flex: 1,
    },
    {
      field: 'start_date',
      headerName: 'Start',
      flex: 1,
      // valueFormatter: ({ value }) => value?.slice(0, 10),
      valueFormatter: ({ value }) => dateFormat(new Date(value?.slice(0, 10))),
    },
    {
      field: 'end_date',
      headerName: 'End',
      flex: 1,
      // valueFormatter: ({ value }) => value?.slice(0, 10),
      valueFormatter: ({ value }) => dateFormat(new Date(value?.slice(0, 10))),
    },
  ];

  return (
    <Fragment>
      <Breadcrumb icon={<CalendarMonth />} title="Manage" parent="School Calendar Terms" />
      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form" noValidate="" onSubmit={onSearch}>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">School Calendar</InputLabel>
                <Select
                  name="school_calendar_id"
                  value={searchInput.school_calendar_id}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="School Calendar"
                >
                  {filterOptions.schoolCalendars.map((calendar, i) => (
                    <MenuItem key={i} value={calendar.id}>
                      {calendar.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Year</InputLabel>
                <Select
                  name="year"
                  value={searchInput.year}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Year"
                >
                  {filterOptions.years.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </form>
        </FilterAccordian>
      </div>
      {!tableData.count && (
        <Alert className="m-15 d-flex gap-3" color="secondary dark">
          <AlertCircle />
          <p>No Terms have been specified for this year yet (Message should disappear when first row is entered)</p>
        </Alert>
      )}
      <button
        onClick={() => {
          setModalState({ update: false, open: true });
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ update: false, open: false });
          setDates(defaultValues);
        }}
        size="lg"
        scrollable={true}
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ update: false, open: false });
            setDates(defaultValues);
          }}
        >
          {modalState.update ? 'Edit' : 'Create'} Calendar Terms
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={onSubmit}>
              <div className="form-group row">
                <h3>{currentCalendar}</h3>
              </div>
              <DateRange
                min={new Date(`${searchInput.year}-01-01`)}
                errors
                views={['month', 'day']}
                max={new Date(`${searchInput.year}-12-31`)}
                from={dates.start_date}
                to={dates.end_date}
                fromOnChange={(val) => onChangeDate('start_date', val)}
                toOnChange={(val) => onChangeDate('end_date', val)}
              />
              <div>
                <button className="btn btn-primary me-1" type="submit">
                  {modalState.update ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.terms}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
          sortModel={[{ field: 'start_date', sort: 'asc' }]}
        />
      </div>
    </Fragment>
  );
};

export default Manage;
